import { useCallback, useEffect, useState } from "react";
import { applyAnalyticsConsent } from "../analytics";
import {
  ConsentDecision,
  ConsentState,
  readConsent,
  readConsentDate,
  writeConsent,
} from "../consent";

/**
 * The consent decision, shared by the banner and the preferences dialog (EB-559).
 *
 * A module-level subscriber list rather than Redux or a context provider: the decision is read by
 * two components that never render together, it has to survive outside React entirely (the tag
 * bootstrap in `index.tsx` reads it before the first render), and it changes at most twice in a
 * session. A store slice for that would be ceremony around a single string.
 */

let current: ConsentState = null;
let initialised = false;
const subscribers = new Set<(state: ConsentState) => void>();

const ensureInitialised = () => {
  if (initialised) return;
  current = readConsent();
  initialised = true;
};

/** Test seam. The module cache persists between tests in a file, and a decision left over from a
 *  previous one would make the next start in the wrong state. */
export const resetConsentCache = () => {
  initialised = false;
  current = null;
  subscribers.clear();
};

export type ConsentApi = {
  /** `null` until the shopper has been asked — which is what the banner keys off. */
  decision: ConsentState;
  /** Whether the banner should be showing. */
  needsDecision: boolean;
  /** ISO date of the stored decision, for the preferences dialog. */
  decidedAt: string | null;
  decide: (decision: ConsentDecision) => void;
};

export const useConsent = (): ConsentApi => {
  ensureInitialised();

  const [decision, setDecision] = useState<ConsentState>(current);

  useEffect(() => {
    subscribers.add(setDecision);
    // A decision made between this component's first render and its subscription — the dialog and
    // the banner can both be mounted — would otherwise be missed.
    setDecision(current);

    return () => {
      subscribers.delete(setDecision);
    };
  }, []);

  const decide = useCallback((next: ConsentDecision) => {
    // Written and applied before the notify, so any subscriber that re-renders synchronously reads
    // the new value rather than the one it is replacing.
    writeConsent(next);
    current = next;
    applyAnalyticsConsent(next === "granted");
    subscribers.forEach((notify) => notify(next));
  }, []);

  return {
    decision,
    needsDecision: decision === null,
    decidedAt: decision === null ? null : readConsentDate(),
    decide,
  };
};
